import React, { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { NATIONALITIES, COUNTRY_DETAILS, COMMON_REQUIREMENTS } from '../data/mock';
import {
  SEO_COUNTRY_BY_SLUG,
  SEO_COUNTRIES,
  LOI_WORDING,
  LOI_LABEL,
  EXEMPTION_WORDING,
} from '../data/seo-content';
import Breadcrumbs from '../components/Breadcrumbs';
import ImmigrationDisclaimer from '../components/ImmigrationDisclaimer';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2, Clock, ShieldCheck, FileCheck2, HelpCircle, AlertCircle } from 'lucide-react';

/**
 * SEO Landing Page — `/singapore-visa/{slug}/`
 *
 * Nationality-specific landing page optimised for organic search.
 * Renders country-specific meta tags, JSON-LD (WebPage, BreadcrumbList,
 * FAQPage), Open Graph, Twitter Card, and a canonical URL. Uses
 * `react-helmet-async` so tags work both at runtime and during static
 * pre-rendering at build time.
 *
 * IMPORTANT — LOI status:
 * The customer-facing LOI wording is driven exclusively by the
 * verified `loi` field in SEO_COUNTRIES (source of truth, verified
 * against official ICA country pages on 2026-02-13). The old
 * `COUNTRY_DETAILS.<code>.loi` boolean has been removed. Any nationality
 * that reaches this page without an SEO_COUNTRIES entry falls back to
 * the defensive 'unknown' wording — NEVER to "Not required".
 */

const SITE_URL = 'https://visasg.com';

const OG_IMAGE_BY_CODE = {
  default: '/og/singapore-visa.jpg',
  cn: '/og/singapore-marina.jpg',
  ru: '/og/singapore-gardens.jpg', kz: '/og/singapore-gardens.jpg',
  uz: '/og/singapore-gardens.jpg', kg: '/og/singapore-gardens.jpg',
  tj: '/og/singapore-gardens.jpg', tm: '/og/singapore-gardens.jpg',
  az: '/og/singapore-gardens.jpg', am: '/og/singapore-gardens.jpg',
  ge: '/og/singapore-gardens.jpg', by: '/og/singapore-gardens.jpg',
  md: '/og/singapore-gardens.jpg', ua: '/og/singapore-gardens.jpg',
  eg: '/og/singapore-marina.jpg', dz: '/og/singapore-marina.jpg',
  iq: '/og/singapore-marina.jpg', ir: '/og/singapore-marina.jpg',
  sy: '/og/singapore-marina.jpg', ye: '/og/singapore-marina.jpg',
  jo: '/og/singapore-marina.jpg', lb: '/og/singapore-marina.jpg',
  ma: '/og/singapore-marina.jpg', tn: '/og/singapore-marina.jpg',
  ly: '/og/singapore-marina.jpg', sd: '/og/singapore-marina.jpg',
  ml: '/og/singapore-marina.jpg',
};

function ogImageFor(code) {
  return `${SITE_URL}${OG_IMAGE_BY_CODE[code] || OG_IMAGE_BY_CODE.default}`;
}

function slugFor(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

const NAT_BY_CODE = NATIONALITIES.reduce((acc, n) => { acc[n.code] = n; return acc; }, {});
const NAT_BY_LEGACY_SLUG = NATIONALITIES.reduce((acc, n) => {
  acc[slugFor(n.name)] = n;
  return acc;
}, {});
const NAT_BY_NEW_SLUG = Object.values(SEO_COUNTRY_BY_SLUG).reduce((acc, c) => {
  const n = NAT_BY_CODE[c.code];
  if (n) acc[c.slug] = { ...n, seo: c };
  return acc;
}, {});

function nationalityBySlug(slug) {
  const s = (slug || '').toLowerCase();
  return NAT_BY_NEW_SLUG[s] || NAT_BY_LEGACY_SLUG[s] || null;
}

function newSlugForCode(code) {
  const rec = Object.values(SEO_COUNTRY_BY_SLUG).find((c) => c.code === code);
  return rec?.slug || null;
}

export default function SingaporeVisaFor() {
  const { slug: legacySlug, segment } = useParams();
  const slug = legacySlug || segment;
  const nat = nationalityBySlug(slug);
  // Business-side intake extras (empty by default; retained only where
  // operationally confirmed — currently empty per PHASE approval).
  const details = COUNTRY_DETAILS[nat?.code || ''] || {};
  const price = nat?.priceFrom || 0;
  // LOI state comes ONLY from the verified SEO_COUNTRIES record.
  // Defensive fallback: if no SEO record, use 'unknown' wording — never
  // default to 'not-required'.
  const seoRecord = nat?.seo;
  const loiState = seoRecord?.loi || 'unknown';
  const loiWording = LOI_WORDING[loiState];
  const loiLabel = LOI_LABEL[loiState];
  const exemptionKey = seoRecord?.exemption || null;

  // FAQs computed above the early-return so hook order is stable.
  const faqs = useMemo(() => {
    if (!nat) return [];
    return [
      {
        q: `How much is the Singapore visa fee for ${nat.name} citizens?`,
        a: `The prescribed ICA visa processing fee is S$30 per application, payable to ICA through the appropriate channel prescribed by ICA. This fee is not payable to Waan Travel Pte. Ltd.`,
      },
      {
        q: `How long does the Singapore visa take?`,
        a: `ICA states that visa applications are processed within three working days, excluding the day of submission. Some applications may take longer to process. Each application is considered on its own merits.`,
      },
      {
        q: `Do ${nat.name} citizens need a Letter of Introduction (LOI)?`,
        a: loiWording,
      },
      {
        q: `What is the period of stay in Singapore?`,
        a: `The period of stay in Singapore is determined by the electronic Visit Pass (e-Pass) issued at the checkpoint by ICA, and is not tied to the validity of the visa. Immigration clearance is decided at the point of entry.`,
      },
      {
        q: `Does VisaSG submit Singapore visa applications on behalf of customers?`,
        a: `No. VisaSG is operated by WAAN TRAVEL PTE. LTD., a Singapore-licensed travel agency (STB Travel Agent Licence No. TA03795). VisaSG/Waan Travel Pte. Ltd. is not an ICA Strategic Partner or an authorised Singapore visa agent and does not submit Singapore visa applications on behalf of customers. Travellers requiring a Singapore visa should apply through the appropriate channel prescribed by ICA.`,
      },
    ];
  }, [nat, loiWording]);

  if (!nat) {
    return <Navigate to="/singapore-visa/countries/" replace />;
  }

  const newSlug = seoRecord?.slug || newSlugForCode(nat.code);
  if (newSlug && slug !== newSlug) {
    return <Navigate to={`/singapore-visa/${newSlug}/`} replace />;
  }

  const canonical = `${SITE_URL}/singapore-visa/${newSlug}/`;
  const demonym = seoRecord?.demonym;
  const title = demonym
    ? `Singapore Visa for ${demonym}s — Requirements & Application | VisaSG`
    : `Singapore Visa for ${nat.name} Passport Holders | VisaSG`;
  const h1 = demonym
    ? `Singapore Visa for ${demonym} Passport Holders`
    : `Singapore Visa for ${nat.name} Passport Holders`;
  const description = `Singapore visa requirements for ${demonym || nat.name} passport holders: documents ICA lists on its country page, prescribed ICA visa fee (S$30) and processing information. VisaSG/Waan Travel Pte. Ltd. is not an ICA Strategic Partner or an authorised Singapore visa agent and does not submit Singapore visa applications on behalf of customers.`;
  const acceptingApplications = false;

  const relatedCountries = NATIONALITIES
    .filter((n) => n.code !== nat.code && n.applicationEnabled !== false)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
    .slice(0, 8);

  // ---- JSON-LD ----
  const ldWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: canonical,
    inLanguage: 'en',
    isPartOf: { '@type': 'WebSite', name: 'Visasg.com', url: SITE_URL },
    about: { '@type': 'Country', name: nat.name },
    provider: {
      '@type': 'TravelAgency',
      name: 'WAAN TRAVEL PTE. LTD.',
      alternateName: 'Visasg.com',
      url: SITE_URL,
      identifier: 'TA03795',
      areaServed: 'SG',
    },
  };
  const ldBreadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL + '/' },
      { '@type': 'ListItem', position: 2, name: 'Singapore Visa', item: SITE_URL + '/singapore-visa/' },
      { '@type': 'ListItem', position: 3, name: 'By nationality', item: SITE_URL + '/singapore-visa/countries/' },
      { '@type': 'ListItem', position: 4, name: h1, item: canonical },
    ],
  };
  const ldFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const ldProduct = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: `Singapore visa application for ${nat.name} citizens`,
    provider: { '@type': 'TravelAgency', name: 'WAAN TRAVEL PTE. LTD.' },
    offers: {
      '@type': 'Offer',
      price: String(price),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/apply?nat=${nat.code}`,
    },
    areaServed: 'SG',
  };

  return (
    <div className="bg-white" data-testid="seo-landing">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Visasg.com" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImageFor(nat.code)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Singapore skyline - visa application for ${nat.name} citizens`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImageFor(nat.code)} />
        <script type="application/ld+json">{JSON.stringify(ldWebPage)}</script>
        <script type="application/ld+json">{JSON.stringify(ldBreadcrumbs)}</script>
        <script type="application/ld+json">{JSON.stringify(ldFaq)}</script>
        <script type="application/ld+json">{JSON.stringify(ldProduct)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-8 pb-2" data-testid="seo-breadcrumb">
        <Breadcrumbs
          items={[
            { name: 'Home', href: '/' },
            { name: 'Singapore Visa', href: '/singapore-visa/' },
            { name: 'By nationality', href: '/singapore-visa/countries/' },
            { name: seoRecord?.demonym ? `${seoRecord.demonym} passport holders` : nat.name },
          ]}
        />
      </section>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-10 md:py-14">
        <div className="text-[11px] uppercase tracking-[0.14em] text-teal-800 font-medium">Singapore eVisa</div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 leading-[1.05] tracking-tight" data-testid="seo-h1">
          {h1}
        </h1>
        <p className="mt-4 md:mt-5 text-neutral-700 text-base md:text-lg max-w-3xl">
          Informational summary of Singapore visa requirements ICA lists for {demonym || nat.name} passport holders. A Singapore entry visa is pre-entry permission to travel to and seek entry into Singapore; it does not itself guarantee entry, which is decided at the checkpoint. <strong>VisaSG/Waan Travel Pte. Ltd. is not an ICA Strategic Partner or an authorised Singapore visa agent and does not submit Singapore visa applications on behalf of customers.</strong> Travellers requiring a Singapore visa should apply through the appropriate channel prescribed by the Immigration &amp; Checkpoints Authority (ICA).
        </p>
        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          <Stat icon={<ShieldCheck size={16} className="text-teal-800" />} label="Prescribed ICA visa fee" value={`S$30 per application`} />
          <Stat icon={<Clock size={16} className="text-teal-800" />} label="Processing time (per ICA)" value="3 working days*" />
          <Stat icon={<FileCheck2 size={16} className="text-teal-800" />} label="Letter of Introduction" value={loiLabel} />
        </div>
        <p className="mt-3 text-xs text-neutral-500 leading-relaxed max-w-3xl">
          *ICA states applications are processed within three working days, excluding the day of submission. Some applications may take longer to process. Fee is payable to ICA through the appropriate channel prescribed by ICA and is not payable to Waan Travel Pte. Ltd.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="https://www.ica.gov.sg/enter-transit-depart/entering-singapore/visa_requirements"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="seo-official-ica-link"
          >
            <Button className="h-12 px-6 bg-teal-900 hover:bg-teal-800 text-white rounded-none font-semibold">
              Official ICA Application Information <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </a>
          <Link to="/singapore-visa/countries/" className="h-12 inline-flex items-center gap-1.5 px-5 rounded-none border border-neutral-300 hover:border-neutral-900 text-neutral-800 font-medium">
            See all countries <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Fee + Processing */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-8 border-t border-neutral-100">
        <h2 className="font-display text-2xl md:text-3xl text-neutral-900 font-semibold">ICA visa fee &amp; processing</h2>
        <div className="mt-4 rounded-2xl border border-neutral-200 overflow-hidden">
          <Row2 label="Prescribed ICA visa processing fee" value="S$30 per application. Payable to ICA through the appropriate channel prescribed by ICA. Not payable to Waan Travel Pte. Ltd." />
          <Row2 label="Processing time (per ICA)" value="ICA processes visa applications within three working days, excluding the day of submission. Some applications may take longer." />
          <Row2 label="Period of stay" value="Determined by the electronic Visit Pass (e-Pass) issued at the checkpoint by ICA. Not tied to the validity of the visa." />
          <Row2 label="Letter of Introduction (LOI)" value={loiWording} />
          <Row2 label="Assessed by" value="The Immigration & Checkpoints Authority (ICA) of Singapore assesses and issues Singapore visas on their individual merits." />
        </div>
        <p className="mt-3 text-[11px] text-neutral-500">Waan Travel Pte. Ltd. does not submit Singapore visa applications on behalf of customers.</p>
      </section>

      {/* Requirements — documents ICA lists */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-8 border-t border-neutral-100">
        <h2 className="font-display text-2xl md:text-3xl text-neutral-900 font-semibold">Documents ICA lists for the visa application</h2>
        <p className="mt-2 text-sm text-neutral-600">Per the ICA country page for {nat.name}. For the current and complete requirements, always check the official ICA source.</p>
        <ul className="mt-4 grid md:grid-cols-2 gap-3" data-testid="ica-required-docs">
          {COMMON_REQUIREMENTS.filter((r) => r.group === 'ica').map((r, i) => (
            <li key={`ica-${i}`} className="rounded-xl border border-neutral-200 p-4">
              <div className="flex items-center gap-2 text-neutral-900 font-medium"><CheckCircle2 size={15} className="text-emerald-600" /> {r.title}</div>
              <div className="mt-1 text-sm text-neutral-600">{r.desc}</div>
            </li>
          ))}
          {loiState === 'required' && (
            <li className="rounded-xl border border-teal-200 bg-teal-50/40 p-4" data-testid="loi-required-card">
              <div className="flex items-center gap-2 text-neutral-900 font-medium"><CheckCircle2 size={15} className="text-emerald-600" /> Letter of Introduction (Form V39A) — listed by ICA</div>
              <div className="mt-1 text-sm text-neutral-700">{loiWording}</div>
            </li>
          )}
        </ul>

        {loiState === 'case-by-case' && (
          <div className="mt-6 rounded-xl border border-neutral-200 bg-neutral-50/60 p-4" data-testid="loi-case-by-case-card">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-teal-800 mt-0.5 shrink-0" />
              <div>
                <div className="text-neutral-900 font-medium">Additional document ICA may request — Letter of Introduction (Form V39A)</div>
                <div className="mt-1 text-sm text-neutral-700">{loiWording}</div>
              </div>
            </div>
          </div>
        )}

        {exemptionKey && (
          <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="passport-exemption-note">
            <div className="font-semibold">Passport exemption note</div>
            <div className="mt-1">
              {EXEMPTION_WORDING[exemptionKey]} Please check the current ICA requirements for your passport type.
            </div>
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-8 border-t border-neutral-100">
        <h2 className="font-display text-2xl md:text-3xl text-neutral-900 font-semibold">Frequently asked questions</h2>
        <div className="mt-4 space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="group rounded-xl border border-neutral-200 p-4">
              <summary className="cursor-pointer flex items-start gap-2 text-neutral-900 font-medium list-none">
                <HelpCircle size={16} className="text-teal-800 mt-0.5" />
                <span className="flex-1">{f.q}</span>
                <span className="text-teal-800 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="mt-3 text-sm text-neutral-700 leading-relaxed pl-6">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Related countries */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-8 border-t border-neutral-100">
        <h2 className="font-display text-2xl md:text-3xl text-neutral-900 font-semibold">Explore other nationalities</h2>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="seo-related">
          {relatedCountries.map((n) => {
            const rSlug = newSlugForCode(n.code);
            if (!rSlug) return null;
            return (
              <Link
                key={n.code}
                to={`/singapore-visa/${rSlug}/`}
                className="text-sm px-3 py-1.5 rounded-full border border-neutral-200 hover:border-teal-800 hover:text-teal-800 text-neutral-700"
              >
                Singapore visa for {n.name}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-teal-950 text-white mt-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 md:py-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-semibold">Applying for a Singapore visa from {nat.name}</h2>
          <p className="mt-3 text-white/85 max-w-2xl mx-auto">
            Travellers requiring a Singapore visa should apply through the appropriate channel prescribed by ICA. Visa applications are assessed by ICA on their individual merits.
          </p>
          <div className="mt-6 flex justify-center flex-wrap gap-3">
            <a
              href="https://www.ica.gov.sg/enter-transit-depart/entering-singapore/visa_requirements"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="h-12 px-7 bg-emerald-400 hover:bg-emerald-300 text-teal-950 rounded-none font-semibold">
                Official ICA Application Information <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </a>
            <Link to="/singapore-visa/countries/">
              <Button className="h-12 px-7 bg-transparent border border-white/40 text-white hover:bg-white/10 rounded-none font-semibold">
                See all countries <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <ImmigrationDisclaimer />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-neutral-200 p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-500">{icon} {label}</div>
      <div className="font-display text-lg md:text-xl text-neutral-900 mt-1.5">{value}</div>
    </div>
  );
}

function Row2({ label, value }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 border-b last:border-b-0 border-neutral-100">
      <div className="p-3 md:p-4 text-[11px] uppercase tracking-widest text-neutral-500 bg-neutral-50">{label}</div>
      <div className="md:col-span-2 p-3 md:p-4 text-neutral-900">{value}</div>
    </div>
  );
}
